"use client";
import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import AppLogo from "@/components/ui/AppLogo";
import Icon from "@/components/ui/AppIcon";
import { CATEGORIES } from "@/lib/data";
import SearchOverlay from "@/components/SearchOverlay";

const navLinks = [
  { href: "/events", label: "Etkinlikler" },
  { href: "/firsatlar", label: "Fırsatlar" },
  { href: "/directory", label: "Haberler" },
  { href: "/advertise", label: "Reklam Ver" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [kesfetOpen, setKesfetOpen] = useState(false);
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const pathname = usePathname();
  const isHome = pathname === "/";
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setKesfetOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navLinkClass = `px-4 py-2 rounded-full text-[17px] font-bold transition-all duration-200 hover:bg-white/10 ${
    scrolled
      ? "text-foreground hover:text-primary hover:bg-primary/10"
      : isHome
      ? "text-white drop-shadow-sm"
      : "text-foreground hover:text-primary hover:bg-primary/10"
  }`;

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled ? "glass-nav shadow-sm border-b border-border py-3" : "py-4 bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 shrink-0">
            <AppLogo src="/assets/images/mykonya-logo-1791058886666.png" size={40} />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {/* Trentleri Keşfet Dropdown */}
            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={() => setKesfetOpen(true)}
              onMouseLeave={() => setKesfetOpen(false)}
            >
              <button
                className={`flex items-center gap-1 ${navLinkClass}`}
                onClick={() => setKesfetOpen((v) => !v)}
                aria-expanded={kesfetOpen}
              >
                Trentleri Keşfet
                <Icon
                  name="ChevronDownIcon"
                  size={16}
                  className={`transition-transform duration-200 ${kesfetOpen ? "rotate-180" : ""}`}
                />
              </button>

              {/* Dropdown Panel */}
              {kesfetOpen && (
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[520px] rounded-2xl shadow-2xl border border-border overflow-hidden"
                  style={{ background: "rgba(255,255,255,0.97)", backdropFilter: "blur(16px)" }}
                >
                  {/* Header */}
                  <div className="px-5 pt-4 pb-3 border-b border-border/60">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Kategoriler</p>
                    <p className="text-sm text-foreground/70 mt-0.5">Konya'daki en iyi yerleri keşfedin</p>
                  </div>

                  {/* Categories Grid */}
                  <div className="grid grid-cols-3 gap-1 p-3">
                    {CATEGORIES.map((cat) => (
                      <Link
                        key={cat.slug}
                        href={`/directory?category=${cat.slug}`}
                        onClick={() => setKesfetOpen(false)}
                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-primary/8 group transition-all duration-150"
                        style={{ color: "var(--foreground)" }}
                      >
                        <span className="text-xl leading-none">{cat.emoji}</span>
                        <span className="text-sm font-medium group-hover:text-primary transition-colors">{cat.label}</span>
                      </Link>
                    ))}
                  </div>

                  {/* Footer CTA */}
                  <div className="px-4 pb-4 pt-1 border-t border-border/60">
                    <Link
                      href="/directory"
                      onClick={() => setKesfetOpen(false)}
                      className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-semibold transition-all"
                      style={{ background: "var(--primary)", color: "#fff" }}
                    >
                      Tüm İşletmeleri Gör
                      <Icon name="ArrowRightIcon" size={15} />
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {navLinks?.map((link) => (
              <Link
                key={link?.href + link?.label}
                href={link?.href}
                className={navLinkClass}
              >
                {link?.label}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            <button
              aria-label="Ara"
              onClick={() => setSearchOpen(true)}
              className={`p-2 rounded-full transition-all ${
                scrolled
                  ? "text-foreground hover:bg-muted"
                  : isHome
                  ? "text-white hover:bg-white/10" :"text-foreground hover:bg-muted"
              }`}
            >
              <Icon name="MagnifyingGlassIcon" size={20} />
            </button>
            <Link
              href="/business-application"
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-all duration-300 cursor-pointer shadow-lg"
              style={{
                background: scrolled ? "var(--primary)" : "rgba(255,255,255,0.95)",
                color: scrolled ? "#fff" : "var(--primary)",
                boxShadow: scrolled ? "0 4px 16px rgba(150,150,150,0.5)" : "0 4px 20px rgba(150,150,150,0.4)",
              }}
            >
              <Icon name="PlusCircleIcon" size={16} />
              Firmanı Ekle
            </Link>
            <button
              aria-label="Menüyü Aç"
              className={`lg:hidden p-2 rounded-full transition-all ${
                scrolled
                  ? "text-foreground hover:bg-muted"
                  : isHome
                  ? "text-white hover:bg-white/10" :"text-foreground hover:bg-muted"
              }`}
              onClick={() => setMobileOpen(true)}
            >
              <Icon name="Bars3Icon" size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[100] flex flex-col" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)" }}>
          <div className="bg-white w-full max-w-sm ml-auto h-full flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <Link href="/" className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
                <AppLogo src="/assets/images/mykonya-logo-1791058886666.png" size={36} />
                <span className="font-bold text-lg">
                  <span className="text-primary">my</span>
                  <span className="text-secondary">konya</span>
                </span>
              </Link>
              <button
                onClick={() => setMobileOpen(false)}
                aria-label="Menüyü Kapat"
                className="p-2 rounded-full hover:bg-muted text-foreground"
              >
                <Icon name="XMarkIcon" size={22} />
              </button>
            </div>
            <nav className="flex flex-col p-4 gap-1 flex-1 overflow-y-auto">
              {/* Trentleri Keşfet mobile accordion */}
              <div>
                <button
                  className="flex items-center justify-between w-full px-4 py-3.5 rounded-xl text-base font-medium text-foreground hover:bg-muted hover:text-primary transition-colors"
                  onClick={() => setMobileCatsOpen((v) => !v)}
                >
                  <span>Trentleri Keşfet</span>
                  <Icon
                    name="ChevronDownIcon"
                    size={16}
                    className={`transition-transform duration-200 ${mobileCatsOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {mobileCatsOpen && (
                  <div className="mt-1 ml-4 grid grid-cols-2 gap-1">
                    {CATEGORIES.map((cat) => (
                      <Link
                        key={cat.slug}
                        href={`/directory?category=${cat.slug}`}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                      >
                        <span>{cat.emoji}</span>
                        <span>{cat.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {navLinks?.map((link) => (
                <Link
                  key={link?.href + link?.label}
                  href={link?.href}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center px-4 py-3.5 rounded-xl text-base font-medium text-foreground hover:bg-muted hover:text-primary transition-colors"
                >
                  {link?.label}
                </Link>
              ))}
              <div className="mt-4 pt-4 border-t border-border">
                <Link
                  href="/business-application"
                  onClick={() => setMobileOpen(false)}
                  className="btn-primary w-full justify-center"
                >
                  <Icon name="PlusCircleIcon" size={18} />
                  Firmanı Ekle
                </Link>
              </div>
            </nav>
          </div>
        </div>
      )}

      <SearchOverlay isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}